import { ComponentProps } from 'react';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';
import { BeneficiaryReviewCard } from './beneficiary-review-card';
import { ConfidenceScoreBadge } from './confidence-score-badge';

type ReviewSectionVariant = 'MATCHED' | 'NOT_MATCHED';

type ReviewSectionConfig =
  | {
      variant: Extract<ReviewSectionVariant, 'MATCHED'>;
      confidenceScore?: number;
      cardConfig: ComponentProps<typeof BeneficiaryReviewCard>;
      isApproveMatchLoading: boolean;
      onApproveMatch: () => Promise<void>;
    }
  | {
      variant: Extract<ReviewSectionVariant, 'NOT_MATCHED'>;
      onChangeMatchedTransaction: ComponentProps<typeof BeneficiaryReviewCard>['onChangeMatchedTransaction'];
    };

type BeneficiaryReviewSectionProps = {
  reviewSectionConfig: ReviewSectionConfig;
};

export const BeneficiaryReviewSection = ({ reviewSectionConfig }: BeneficiaryReviewSectionProps) => {
  const { variant } = reviewSectionConfig;

  switch (variant) {
    case 'MATCHED':
      return (
        <Stack space={8} align="stretch">
          <Stack space={4} align="stretch">
            <Inline justify="between">
              <h3 className="text-base leading-6 font-medium tracking-tight text-black">Matched transaction</h3>

              <ConfidenceScoreBadge confidenceScore={reviewSectionConfig.confidenceScore} />
            </Inline>

            <BeneficiaryReviewCard {...reviewSectionConfig.cardConfig} />
          </Stack>

          <Button
            variant="default"
            isLoading={reviewSectionConfig.isApproveMatchLoading}
            onClick={reviewSectionConfig.onApproveMatch}
          >
            Approve Match
          </Button>
        </Stack>
      );
    case 'NOT_MATCHED':
      return (
        <Stack space={4} align="stretch">
          <h3 className="text-base leading-6 font-medium tracking-tight text-black">
            It looks like you've paid this invoice, but we couldn't find a matching transaction.
          </h3>

          <Button variant="default" onClick={reviewSectionConfig.onChangeMatchedTransaction}>
            Set matched transaction
          </Button>
        </Stack>
      );
    default:
      return exhaustiveCheck(variant);
  }
};
